import React, { useState, useEffect, useMemo, useCallback } from "react";

/**
 * Visual Date Range Picker for TDR flexibility options
 * Updated to match Single ClickToFlyResult page UI/UX
 * Uses single-click directional range selection
 */
const VisualDateRangePicker = ({
  options,
  selectedValue,
  onSelect,
  defaultPrice,
  departureDate,
}) => {
  const [selectedStart, setSelectedStart] = useState(null);
  const [selectedEnd, setSelectedEnd] = useState(null);

  // Initialize with departure date (exact date selected)
  useEffect(() => {
    if (departureDate && options.length > 0) {
      const depDate = new Date(departureDate);
      depDate.setHours(0, 0, 0, 0);
      setSelectedStart(depDate);
      setSelectedEnd(depDate);
      
      // Select the firm departure (1-day) option
      const firmOption = options.find((opt) => opt.tdrDays === 1);
      if (firmOption) {
        onSelect(firmOption);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departureDate]);

  // Generate 5 dates: center date ± 2 days
  const dates = useMemo(() => {
    if (!departureDate) return [];
    const original = new Date(departureDate);
    original.setHours(0, 0, 0, 0);
    
    return [-2, -1, 0, 1, 2].map((offset) => {
      const date = new Date(original);
      date.setDate(original.getDate() + offset);
      return {
        date,
        offset,
        isOriginal: offset === 0,
        dayName: date.toLocaleDateString("en-US", { weekday: "short" }),
        dayNum: date.getDate().toString(),
        month: date.toLocaleDateString("en-US", { month: "short" }),
      };
    });
  }, [departureDate]);

  // Calculate TDR days from current selection
  const tdrDays = useMemo(() => {
    if (!selectedStart || !selectedEnd) return 1;
    const start = new Date(selectedStart);
    const end = new Date(selectedEnd);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return Math.min(diffDays, 3);
  }, [selectedStart, selectedEnd]);

  // Get discount for current TDR days
  const currentDiscount = useMemo(() => {
    if (!options || options.length === 0) return null;
    return options.find((o) => o.tdrDays === tdrDays);
  }, [options, tdrDays]);

  // Update parent when selection changes
  useEffect(() => {
    if (currentDiscount) {
      onSelect(currentDiscount);
    }
  }, [currentDiscount, onSelect]);

  // Single click: set range from original to clicked date
  const handleDateClick = useCallback((dateInfo) => {
    if (!departureDate) return;
    
    const original = new Date(departureDate);
    original.setHours(0, 0, 0, 0);
    const clicked = new Date(dateInfo.date);
    clicked.setHours(0, 0, 0, 0);

    if (dateInfo.isOriginal) {
      // Clicked original = exact date
      setSelectedStart(original);
      setSelectedEnd(original);
    } else if (dateInfo.offset < 0) {
      // Clicked before original: range is clicked -> original
      setSelectedStart(clicked);
      setSelectedEnd(original);
    } else {
      // Clicked after original: range is original -> clicked
      setSelectedStart(original);
      setSelectedEnd(clicked);
    }
  }, [departureDate]);

  // Check if a date is in the selected range
  const isInRange = useCallback((dateInfo) => {
    if (!selectedStart || !selectedEnd) return false;
    const d = new Date(dateInfo.date);
    d.setHours(0, 0, 0, 0);
    const start = new Date(selectedStart);
    const end = new Date(selectedEnd);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return d >= start && d <= end;
  }, [selectedStart, selectedEnd]);

  // Check if a date is an edge of the range (and not a single date)
  const isEdge = useCallback((dateInfo) => {
    if (!selectedStart || !selectedEnd) return false;
    const d = new Date(dateInfo.date);
    d.setHours(0, 0, 0, 0);
    const start = new Date(selectedStart);
    const end = new Date(selectedEnd);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    
    // Only mark as edge if range has more than one day
    if (start.getTime() === end.getTime()) return false;
    
    return d.getTime() === start.getTime() || d.getTime() === end.getTime();
  }, [selectedStart, selectedEnd]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.abs(amount || 0));
  };

  // Format percentage
  const formatPercent = (decimal) => {
    return `${(Math.abs(decimal || 0) * 100).toFixed(1)}%`;
  };

  // Get label for current selection
  const getFlexLabel = () => {
    if (tdrDays === 1) return "Exact Date";
    return `${tdrDays}-Day Window`;
  };

  // Format date for summary
  const formatDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Calculate max savings for header text
  const maxSavings = useMemo(() => {
    if (!options || options.length === 0) return 0;
    return Math.max(...options.map((o) => Math.abs(o.change?.dollars || 0)));
  }, [options]);

  return (
    <div className="visual-date-range-picker">
      <label className="toggle-label">Is your departure date flexible?</label>
      <p className="toggle-description">
        Click a date to set your flexibility window and save up to {formatCurrency(maxSavings)}
      </p>

      <div className="date-range-picker">
        <div className="date-range-header">
          <span className="picker-label">Click a date to set your flexibility window:</span>
        </div>
        
        <div className="date-range-days">
          {dates.map((d, i) => (
            <div
              key={i}
              className={`date-day ${isInRange(d) ? "in-range" : ""} ${isEdge(d) ? "is-edge" : ""} ${d.isOriginal ? "is-original" : ""}`}
              onClick={() => handleDateClick(d)}
            >
              <span className="day-name">{d.dayName}</span>
              <span className="day-num">{d.dayNum}</span>
              <span className="day-month">{d.month}</span>
              {d.isOriginal && <span className="original-badge">Requested</span>}
            </div>
          ))}
        </div>
        
        <div className="date-range-summary">
          <span className="range-display">
            <strong>{getFlexLabel()}</strong>
            {tdrDays > 1 && selectedStart && selectedEnd && (
              <span className="range-dates" style={{ marginLeft: "10px" }}>
                ({formatDate(selectedStart)} - {formatDate(selectedEnd)})
              </span>
            )}
          </span>
          {currentDiscount && currentDiscount.change?.percent !== 0 ? (
            <span className="range-discount text-success">
              Save {formatPercent(currentDiscount.change?.percent)} ({formatCurrency(currentDiscount.change?.dollars)})
            </span>
          ) : (
            <span className="range-discount text-muted">No flexibility discount</span>
          )}
        </div>
      </div>

      {/* Info cards showing all TDR options */}
      <div className="tdr-options tdr-info-only" style={{ marginTop: "1rem" }}>
        {options.map((option) => {
          const savings = Math.abs(option.change?.dollars || 0);
          const percent = Math.abs(option.change?.percent || 0);
          const isSelected = tdrDays === option.tdrDays;

          return (
            <div
              key={option.tdrDays}
              className={`tdr-option ${isSelected ? "selected" : ""}`}
            >
              <div className="tdr-option-header">
                <div className="tdr-days-label">
                  {option.tdrDays === 1
                    ? "Firm Departure"
                    : `${option.tdrDays}-Day Flexible`}
                </div>
                <div className="tdr-savings">
                  {percent > 0 ? (
                    <span className="discount">
                      Save {formatCurrency(savings)} ({formatPercent(percent)})
                    </span>
                  ) : (
                    <span className="neutral">No discount</span>
                  )}
                </div>
              </div>
              {option.tdrDays > 1 && (
                <div className="tdr-description">
                  You'll be notified of your departure date within 48hr after
                  booking
                  {option.tdrDays === 3
                    ? ", on or before your selected date"
                    : ""}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VisualDateRangePicker;
